import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import QrCanvas from '../components/QrCanvas';
import PinMarker from '../components/PinMarker';
import PinLegend from '../components/PinLegend';
import { Wrench, ArrowCounterClockwise, Copy, CheckCircle, X, ShareFat, QrCode, MapTrifold, MapPin, FileText, CircleNotch, CaretDown, CaretUp } from '@phosphor-icons/react';
import { BuilderResponseControls, BuilderResponseStrip } from '../components/BuilderResponseControls';
import { AnnotationOverlay } from '../components/PhotoAnnotator';
import { generateBuilderPdf } from '../lib/builderPdf';
import { toast } from 'sonner';

export default function Shared() {
    const { shortId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [marks, setMarks] = useState({}); // shortId -> 'fixed' | 'reopen'
    const [replyOpen, setReplyOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const [lightboxAnnotations, setLightboxAnnotations] = useState([]);
    const openLightbox = useCallback((src, annotations = []) => {
        setLightboxSrc(src);
        setLightboxAnnotations(annotations || []);
    }, []);
    const closeLightbox = useCallback(() => {
        setLightboxSrc(null);
        setLightboxAnnotations([]);
    }, []);
    const [activePin, setActivePin] = useState(null); // { floorId, snagId }
    const [pulseTick, setPulseTick] = useState(0);   // bump to re-trigger pulse
    const [builderName, setBuilderName] = useState(() => {
        try { return localStorage.getItem('defectr:builder-name') || ''; } catch { return ''; }
    });
    const [reloadTick, setReloadTick] = useState(0);
    const [severityFilter, setSeverityFilter] = useState('all'); // all | cosmetic | minor | major | urgent
    const [roomFilter, setRoomFilter] = useState('all');
    const [responseFilter, setResponseFilter] = useState('all'); // all | awaiting | rejected | approved | disputed
    const [sortBy, setSortBy] = useState('pin'); // pin | severity | recent
    const [pdfBuilding, setPdfBuilding] = useState(false);
    const [pdfReady, setPdfReady] = useState(null); // { url, filename }

    // Persist the builder's name across reloads — once they've typed it
    // for one approval it's reused for every subsequent one.
    useEffect(() => {
        try { localStorage.setItem('defectr:builder-name', builderName); } catch { /* noop */ }
    }, [builderName]);

    // Called from a snag row's "Locate on plan" button. Opens the pin's
    // tooltip, scrolls the floorplan into view, and pulses the pin for 1s.
    const locateSnag = (s) => {
        if (!s?.pin?.floorId) return;
        setActivePin({ floorId: s.pin.floorId, snagId: s.id });
        setPulseTick(t => t + 1);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!shortId) { setError('Invalid share link'); return; }
            try {
                const res = await axios.get(`${API}/share/${shortId}`);
                if (cancelled) return;
                setData(res.data?.payload || null);
                if (!res.data?.payload) setError('Report not found');
            } catch (err) {
                if (cancelled) return;
                const status = err?.response?.status;
                setError(status === 404 ? 'This share link has expired or does not exist.' : 'Could not load report. Please try again.');
            }
        })();
        return () => { cancelled = true; };
    }, [shortId, reloadTick]);

    const effective = useMemo(() => {
        if (!data) return { out: [], done: [], all: [], rooms: [] };
        const all = data.snags.map(s => {
            const m = marks[s.id];
            let status = s.status;
            if (m === 'fixed') status = 'completed';
            else if (m === 'reopen') status = 'outstanding';
            return { ...s, status };
        });
        const rooms = Array.from(new Set(all.map(s => s.room).filter(r => r && r !== '—'))).sort();

        // Filter pipeline (list views only — floorplan always shows all pins)
        const sevRank = { urgent: 0, major: 1, minor: 2, cosmetic: 3 };
        const filterAndSort = (list) => {
            let out = list;
            if (severityFilter !== 'all') out = out.filter(s => (s.severity || 'minor') === severityFilter);
            if (roomFilter !== 'all') out = out.filter(s => s.room === roomFilter);
            if (responseFilter === 'awaiting') {
                out = out.filter(s => !s.builder_response && s.status === 'outstanding');
            } else if (responseFilter === 'rejected') {
                out = out.filter(s => s.builder_response === 'rejected');
            } else if (responseFilter === 'approved') {
                out = out.filter(s => s.builder_response === 'approved');
            } else if (responseFilter === 'disputed') {
                out = out.filter(s => !!s.disputed);
            }
            const sorted = [...out];
            if (sortBy === 'severity') {
                sorted.sort((a, b) =>
                    (sevRank[a.severity || 'minor'] - sevRank[b.severity || 'minor'])
                    || ((a.pinNumber ?? 9999) - (b.pinNumber ?? 9999)),
                );
            } else if (sortBy === 'recent') {
                sorted.sort((a, b) => {
                    const ta = a.created_at || '';
                    const tb = b.created_at || '';
                    return tb.localeCompare(ta);
                });
            } else { // pin
                sorted.sort((a, b) => (a.pinNumber ?? 9999) - (b.pinNumber ?? 9999));
            }
            return sorted;
        };
        return {
            out: filterAndSort(all.filter(s => s.status === 'outstanding')),
            done: filterAndSort(all.filter(s => s.status === 'completed')),
            all,
            rooms,
        };
    }, [data, marks, severityFilter, roomFilter, responseFilter, sortBy]);

    const filtersActive = severityFilter !== 'all' || roomFilter !== 'all' || responseFilter !== 'all';

    const handleBuildPdf = async () => {
        if (!data || pdfBuilding) return;
        setPdfBuilding(true);
        setPdfReady(null);
        try {
            const out = await generateBuilderPdf(data, {
                builderName,
                builderEmail: '',
                downloadedAt: new Date(),
            });
            setPdfReady(out);
            toast.success('Builder PDF ready — tap Download.');
        } catch (err) {
            console.error('Builder PDF failed', err);
            toast.error('Could not build the PDF. Please try again.');
        } finally {
            setPdfBuilding(false);
        }
    };

    useEffect(() => {
        if (!lightboxSrc) return;
        const onKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxSrc, closeLightbox]);

    if (error) return (
        <div className="min-h-screen bg-white text-[#09090B] flex flex-col">
            <header className="border-b border-[#18181B] bg-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2">
                    <img
                        src="/defectr-logo.png"
                        alt="DefectR"
                        className="h-9 w-auto object-contain"
                    />
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">Builder reply portal</span>
                </div>
            </header>
            <main className="flex-1 flex items-center justify-center px-6">
                <div className="max-w-md w-full border border-[#18181B] p-8 text-center" data-testid="shared-error">
                    <h1 className="font-display text-2xl font-bold mb-2">Link unavailable</h1>
                    <p className="text-sm text-[#52525B]">{error}</p>
                    <p className="text-xs text-[#52525B] mt-4">Share links expire 90 days after being generated. Ask the homeowner to regenerate the link from DefectR.</p>
                    <a
                        href="/"
                        data-testid="shared-error-home-link"
                        className="mt-6 inline-block bg-[#1FA5CF] text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                    >
                        Return to DefectR
                    </a>
                </div>
            </main>
        </div>
    );
    if (!data) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

    const toggleMark = (snagId, originalStatus) => {
        setMarks(prev => {
            const cur = prev[snagId];
            // 3-state cycle: undefined -> opposite of original -> back to undefined (clear)
            if (originalStatus === 'completed') {
                // already completed: builder can mark "reopen" (outstanding)
                return cur === 'reopen' ? { ...prev, [snagId]: undefined } : { ...prev, [snagId]: 'reopen' };
            }
            return cur === 'fixed' ? { ...prev, [snagId]: undefined } : { ...prev, [snagId]: 'fixed' };
        });
    };

    const fixedIds = Object.entries(marks).filter(([, v]) => v === 'fixed').map(([id]) => id);
    const reopenIds = Object.entries(marks).filter(([, v]) => v === 'reopen').map(([id]) => id);
    const totalChanges = fixedIds.length + reopenIds.length;

    const replyPayload = {
        v: 1,
        r: data.r || '',
        f: fixedIds,
        u: reopenIds,
        at: Date.now(),
    };
    const replyCode = btoa(unescape(encodeURIComponent(JSON.stringify(replyPayload))));

    const copyCode = async () => {
        try { await navigator.clipboard.writeText(replyCode); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
    };

    return (
        <div className="min-h-screen bg-white text-[#09090B] pb-24">
            <header className="border-b border-[#18181B] sticky top-0 bg-white z-30">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2">
                    <img
                        src="/defectr-logo.png"
                        alt="DefectR"
                        className="h-9 w-auto object-contain"
                    />
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">Builder reply portal</span>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <section className="border border-[#18181B] p-6">
                    <h1 className="font-display text-3xl font-bold">Snag Report</h1>
                    <p className="text-sm text-[#52525B]">{new Date(data.date).toLocaleDateString()}</p>
                    <div className="mt-4 text-sm font-mono uppercase tracking-[0.18em]">
                        <div>{data.name}</div>
                        {data.address && <div className="text-[#52525B]">{data.address}</div>}
                        <div className="text-[#52525B]">By {data.reporter?.name}</div>
                    </div>

                    <div className="grid grid-cols-2 mt-6 border-t border-b border-[#18181B]">
                        <div className="p-3 border-r border-[#18181B]">
                            <div className="label-tiny">Outstanding</div>
                            <div className="font-display text-2xl font-bold text-[#E53935]" data-testid="shared-stat-outstanding">{effective.out.length}</div>
                        </div>
                        <div className="p-3">
                            <div className="label-tiny">Completed</div>
                            <div className="font-display text-2xl font-bold text-[#00875A]" data-testid="shared-stat-completed">{effective.done.length}</div>
                        </div>
                    </div>

                    {data.r ? (
                        <div className="mt-4 border border-[#1FA5CF] bg-[#E8F5FB] p-3 text-xs text-[#52525B] flex items-start gap-2">
                            <Wrench size={16} weight="duotone" color="#1FA5CF" className="mt-0.5 flex-none" />
                            <div>
                                <div className="font-display font-semibold text-[#09090B] text-sm">Builder?</div>
                                Tap <strong>Approve</strong> or <strong>Reject</strong> on any defect to send your decision back to the homeowner instantly. Tap <strong>Mark fixed</strong> when the work is complete and reply with a QR when you&apos;re ready.
                            </div>
                        </div>
                    ) : (
                        <div className="mt-4 border border-[#E4E4E7] p-3 text-xs text-[#52525B]">
                            This is an older share link without a reply channel. Ask the homeowner to regenerate the link to enable builder replies.
                        </div>
                    )}

                    {/* Builder-side PDF export */}
                    <div className="mt-4 border border-[#E4E4E7] p-3 flex items-center gap-3 flex-wrap" data-testid="builder-pdf-card">
                        <FileText size={20} weight="duotone" color="#1FA5CF" className="flex-none" />
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-semibold text-sm text-[#09090B]">Download builder copy (PDF)</div>
                            <div className="text-xs text-[#52525B]">Snag list with photos, severity & response status. Sorted urgent first.</div>
                        </div>
                        {pdfReady ? (
                            <a
                                href={pdfReady.url}
                                download={pdfReady.filename}
                                data-testid="builder-pdf-download"
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1FA5CF] text-white font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                            >
                                <FileText size={12} weight="bold" /> Download
                            </a>
                        ) : (
                            <button
                                type="button"
                                onClick={handleBuildPdf}
                                disabled={pdfBuilding}
                                data-testid="builder-pdf-build"
                                className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#18181B] text-[#09090B] hover:bg-[#F4F4F5] font-mono text-[10px] uppercase tracking-[0.18em] active:scale-95 transition-all duration-150 disabled:opacity-50 disabled:cursor-wait"
                            >
                                {pdfBuilding ? (
                                    <><CircleNotch size={12} weight="bold" className="animate-spin" /> Building…</>
                                ) : (
                                    <><FileText size={12} weight="bold" /> Build PDF</>
                                )}
                            </button>
                        )}
                    </div>

                    {data.r && (
                        <div className="mt-3" data-testid="builder-name-block">
                            <label className="label-tiny block">Your name (optional)</label>
                            <input
                                value={builderName}
                                onChange={(e) => setBuilderName(e.target.value)}
                                placeholder="e.g. Dave – Site Manager"
                                data-testid="builder-name-input"
                                className="w-full mt-1 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2 text-sm"
                                maxLength={120}
                            />
                            <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[#A1A1AA]">
                                Shown to the homeowner against each approval / rejection.
                            </p>
                        </div>
                    )}

                    <FloorplansSection
                        data={data}
                        effective={effective}
                        activePin={activePin}
                        setActivePin={setActivePin}
                        pulseTick={pulseTick}
                    />

                    <SharedFilterBar
                        rooms={effective.rooms}
                        severity={severityFilter}
                        setSeverity={setSeverityFilter}
                        room={roomFilter}
                        setRoom={setRoomFilter}
                        response={responseFilter}
                        setResponse={setResponseFilter}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        active={filtersActive}
                        onReset={() => { setSeverityFilter('all'); setRoomFilter('all'); setResponseFilter('all'); }}
                    />

                    <Group title="Outstanding defects" items={effective.out} color="#E53935" marks={marks} onToggle={toggleMark} canMark={!!data.r} onPhotoClick={openLightbox} onLocate={locateSnag} shortId={shortId} builderName={builderName} onResponded={() => setReloadTick(t => t + 1)} />
                    <Group title="Completed defects" items={effective.done} color="#00875A" marks={marks} onToggle={toggleMark} canMark={!!data.r} onPhotoClick={openLightbox} onLocate={locateSnag} shortId={shortId} builderName={builderName} onResponded={() => setReloadTick(t => t + 1)} />
                </section>
            </main>

            {data.r && (
                <div
                    className="fixed bottom-0 left-0 right-0 border-t border-[#18181B] bg-white z-30 no-print"
                    style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                >
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="label-tiny">Pending reply</div>
                            <div className="font-display text-sm">
                                {totalChanges === 0 ? 'No changes yet' : `${fixedIds.length} fixed${reopenIds.length ? `, ${reopenIds.length} reopened` : ''}`}
                            </div>
                        </div>
                        <button
                            disabled={totalChanges === 0}
                            onClick={() => setReplyOpen(true)}
                            data-testid="generate-reply-btn"
                            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] active:scale-95 transition-all duration-150 ${totalChanges === 0 ? 'bg-[#E4E4E7] text-[#A1A1AA] cursor-not-allowed' : 'bg-[#1FA5CF] text-white hover:bg-[#1787AB]'}`}
                        >
                            <QrCode size={16} weight="bold" /> Generate reply
                        </button>
                    </div>
                </div>
            )}

            {replyOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 no-print" data-testid="reply-modal">
                    <div className="bg-white w-full max-w-md border border-[#18181B]">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-[#18181B]">
                            <div className="flex items-center gap-2">
                                <ShareFat size={18} weight="bold" />
                                <span className="font-display font-semibold">Reply ready</span>
                            </div>
                            <button onClick={() => setReplyOpen(false)} data-testid="close-reply-modal" className="p-1 hover:bg-[#F4F4F5]"><X size={18} /></button>
                        </div>
                        <div className="p-5 text-center">
                            <p className="text-sm text-[#52525B]">Show this QR to the homeowner, or send them the code.</p>
                            <div className="mt-4 flex justify-center" data-testid="reply-qr">
                                <QrCanvas value={replyCode} size={240} />
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-3 text-xs font-mono uppercase tracking-[0.18em]">
                                <div className="border border-[#E4E4E7] p-2"><span className="text-[#52525B]">Fixed</span><div className="text-[#00875A] font-bold text-base">{fixedIds.length}</div></div>
                                <div className="border border-[#E4E4E7] p-2"><span className="text-[#52525B]">Reopened</span><div className="text-[#E53935] font-bold text-base">{reopenIds.length}</div></div>
                            </div>
                            <div className="mt-4">
                                <div className="label-tiny mb-2">Or paste code</div>
                                <textarea
                                    readOnly
                                    value={replyCode}
                                    rows={3}
                                    className="w-full border border-[#E4E4E7] px-3 py-2 font-mono text-[10px] break-all"
                                    data-testid="reply-code-text"
                                />
                                <button
                                    onClick={copyCode}
                                    data-testid="copy-reply-btn"
                                    className="mt-2 w-full bg-[#1FA5CF] text-white py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2"
                                >
                                    {copied ? <><CheckCircle size={14} weight="fill" /> Copied</> : <><Copy size={14} /> Copy code</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {lightboxSrc && (
                <div
                    className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 no-print"
                    onClick={closeLightbox}
                    data-testid="shared-photo-lightbox"
                >
                    <button
                        onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                        className="absolute top-4 right-4 p-2 bg-white/10 text-white hover:bg-white/20 active:scale-95 transition-all duration-150"
                        data-testid="close-photo-lightbox"
                        aria-label="Close photo"
                    >
                        <X size={20} weight="bold" />
                    </button>
                    <div
                        className="inline-flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <AnnotationOverlay
                            photoSrc={lightboxSrc}
                            annotations={lightboxAnnotations}
                            className="max-w-[95vw] max-h-[90vh] object-contain select-none"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

function Group({ title, items, color, marks, onToggle, canMark, onPhotoClick, onLocate, shortId, builderName, onResponded }) {
    if (items.length === 0) return null;
    const markClass = (mark) => {
        if (mark === 'fixed') return 'bg-[#00875A] text-white';
        if (mark === 'reopen') return 'bg-[#E53935] text-white';
        return 'border border-[#18181B] text-[#09090B] hover:bg-[#F4F4F5]';
    };
    const pinnedSuffix = (pin, floorName) => {
        if (!pin) return '';
        if (floorName) return ` · pinned on ${floorName}`;
        return ' · pinned on plan';
    };
    return (
        <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 inline-block" style={{ background: color }} />
                <h3 className="font-display font-semibold uppercase tracking-wider text-sm">{title}</h3>
                <span className="font-mono text-xs text-[#52525B]">({items.length})</span>
            </div>
            <ul className="divide-y divide-[#E4E4E7] border border-[#E4E4E7]">
                {items.map((s, i) => {
                    const mark = marks[s.id];
                    return (
                        <li key={s.id || i} className="p-3 flex flex-wrap sm:flex-nowrap items-start gap-3">
                            {s.photo && (
                                <button
                                    type="button"
                                    onClick={() => onPhotoClick?.(s.photo, s.photo_annotations || [])}
                                    data-testid={`shared-photo-${s.id}`}
                                    aria-label="View photo"
                                    className="flex-none p-0 border border-[#E4E4E7] hover:ring-2 hover:ring-[#1FA5CF] active:scale-95 transition-all duration-150"
                                >
                                    <AnnotationOverlay
                                        photoSrc={s.photo}
                                        annotations={s.photo_annotations || []}
                                        className="w-16 h-16 object-cover block"
                                    />
                                </button>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-semibold">
                                    {s.pinNumber && (
                                        <span className="inline-flex items-center justify-center w-5 h-5 bg-[#1FA5CF] text-white font-mono text-[10px] font-bold mr-2 align-middle">{s.pinNumber}</span>
                                    )}
                                    {s.title}
                                </div>
                                <div className="text-xs text-[#52525B]">{s.room}{s.notes ? ` · ${s.notes}` : ''}{pinnedSuffix(s.pin, s.floor)}</div>
                                {s.ai_analysis && (
                                    <SharedAiVerdict verdict={s.ai_analysis} testId={`shared-ai-${s.id}`} />
                                )}
                                <BuilderResponseStrip snag={s} />
                                {canMark && shortId && (
                                    <BuilderResponseControls
                                        shortId={shortId}
                                        snag={s}
                                        builderName={builderName}
                                        onResponded={onResponded}
                                    />
                                )}
                            </div>
                            {/* Action buttons — on mobile they wrap to a new
                                row (w-full basis-full) so the AI Verdict
                                card above gets the full row width. On sm+
                                they sit inline on the right. */}
                            <div className="basis-full sm:basis-auto flex sm:flex-col items-center gap-2 sm:gap-1 shrink-0 justify-end w-full sm:w-auto">
                                {s.pin?.floorId && (
                                    <button
                                        type="button"
                                        onClick={() => onLocate?.(s)}
                                        data-testid={`locate-snag-${s.id}`}
                                        aria-label="Locate on plan"
                                        title="Locate on plan"
                                        className="inline-flex items-center justify-center w-9 h-9 border border-[#E4E4E7] text-[#1FA5CF] hover:bg-[#E8F5FB] hover:border-[#1FA5CF] active:scale-95 transition-all duration-150"
                                    >
                                        <MapPin size={16} weight="fill" />
                                    </button>
                                )}
                                {canMark && (
                                    <button
                                        onClick={() => onToggle(s.id, color === '#00875A' ? 'completed' : 'outstanding')}
                                        data-testid={`mark-snag-${s.id}`}
                                        className={`inline-flex items-center gap-1 px-2 py-2 font-mono text-[10px] uppercase tracking-wider whitespace-nowrap active:scale-95 transition-all duration-150 ${markClass(mark)}`}
                                    >
                                        {mark === 'fixed' && <><CheckCircle size={12} weight="fill" /> Fixed</>}
                                        {mark === 'reopen' && <><ArrowCounterClockwise size={12} weight="bold" /> Reopen</>}
                                        {!mark && (color === '#00875A' ? <><ArrowCounterClockwise size={12} weight="bold" /> Reopen</> : <><Wrench size={12} weight="bold" /> Mark fixed</>)}
                                    </button>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function FloorplansSection({ data, effective, activePin, setActivePin, pulseTick }) {
    const floorplans = (data.floorplans || []).filter(f => f.image);
    const pinRefs = useRef({});  // snagId -> element
    const [pulsingId, setPulsingId] = useState(null);

    // Close active tooltip when clicking the backdrop (anywhere outside pins)
    useEffect(() => {
        if (!activePin) return;
        const onClick = () => setActivePin(null);
        const onKey = (e) => { if (e.key === 'Escape') setActivePin(null); };
        // Use a microtask delay so the initial click that opened the tooltip
        // isn't the one that closes it.
        const t = setTimeout(() => window.addEventListener('click', onClick), 0);
        window.addEventListener('keydown', onKey);
        return () => {
            clearTimeout(t);
            window.removeEventListener('click', onClick);
            window.removeEventListener('keydown', onKey);
        };
    }, [activePin, setActivePin]);

    // When a snag row "Locate on plan" button fires, pulseTick ticks up.
    // Scroll the matching pin into view and pulse it for 1s.
    useEffect(() => {
        if (!activePin || !pulseTick) return;
        const el = pinRefs.current[activePin.snagId];
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setPulsingId(activePin.snagId);
        const t = setTimeout(() => setPulsingId(null), 1200);
        return () => clearTimeout(t);
    }, [pulseTick, activePin]);

    if (floorplans.length === 0) return null;

    // Look up the live effective status for each snag by id so pin colour
    // matches any builder Mark-fixed toggles already made in this session.
    const snagById = new Map(effective.all.map(s => [s.id, s]));

    return (
        <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
                <MapTrifold size={16} weight="bold" color="#1FA5CF" />
                <h3 className="font-display font-semibold uppercase tracking-wider text-sm">Floor plans</h3>
                <span className="font-mono text-xs text-[#52525B]">({floorplans.length})</span>
            </div>
            <div className="space-y-6">
                {floorplans.map(floor => {
                    const pinsOnFloor = data.snags
                        .filter(s => s.pin && s.pin.floorId === floor.id)
                        .map(s => ({
                            ...s,
                            // Use live effective status (reflecting builder marks)
                            status: snagById.get(s.id)?.status || s.status,
                        }));
                    return (
                        <div key={floor.id} className="border border-[#18181B]">
                            <div className="px-3 py-2 border-b border-[#18181B] flex items-center justify-between">
                                <div className="font-display font-semibold">{floor.name}</div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">
                                    {pinsOnFloor.length} pin{pinsOnFloor.length === 1 ? '' : 's'}
                                </div>
                            </div>
                            <div className="relative bg-[#F4F4F5]">
                                <img src={floor.image} alt={floor.name} className="w-full block select-none" draggable={false} />
                                {pinsOnFloor.map(s => {
                                    const isActive = activePin?.floorId === floor.id && activePin?.snagId === s.id;
                                    const isPulsing = pulsingId === s.id;
                                    // Flip tooltip to the left half when pin sits in the right 50% so it stays on-plan
                                    const flipX = s.pin.x > 50;
                                    return (
                                        <div
                                            key={s.id}
                                            ref={el => { if (el) pinRefs.current[s.id] = el; }}
                                            style={{ left: `${s.pin.x}%`, top: `${s.pin.y}%` }}
                                            // Raise the wrapper when active / hovered so the tooltip sits
                                            // above sibling pins and their own wrappers.
                                            className={`absolute -translate-x-1/2 -translate-y-full group ${isActive ? 'z-40' : 'z-10 hover:z-40'}`}
                                        >
                                            {isPulsing && (
                                                <span
                                                    aria-hidden
                                                    className="absolute inset-0 -m-2 rounded-full ring-4 ring-[#1FA5CF] animate-ping pointer-events-none"
                                                />
                                            )}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActivePin(isActive ? null : { floorId: floor.id, snagId: s.id });
                                                }}
                                                data-testid={`shared-pin-${s.id}`}
                                                aria-label={`Pin ${s.pinNumber || ''} — ${s.title}`}
                                                className="block p-0 bg-transparent border-0 cursor-pointer"
                                            >
                                                <PinMarker number={s.pinNumber} status={s.status} builderResponse={s.builder_response} severity={s.severity} />
                                            </button>
                                            {/* Hover preview (desktop) — replaced by click popover when active */}
                                            <div
                                                className={`absolute z-20 ${flipX ? 'right-1/2 translate-x-4 mr-2' : 'left-1/2 -translate-x-4 ml-2'} -translate-y-1/2 top-[-10px] w-56 bg-white border border-[#18181B] shadow-lg p-3 pointer-events-none transition-opacity duration-150 ${isActive ? 'opacity-100 pointer-events-auto' : 'opacity-0 group-hover:opacity-100'}`}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className={`inline-flex items-center justify-center w-5 h-5 font-mono text-[10px] font-bold text-white ${s.status === 'completed' ? 'bg-[#00875A]' : 'bg-[#E53935]'}`}>
                                                        {s.pinNumber || '?'}
                                                    </span>
                                                    <span className="font-display font-semibold text-sm truncate">{s.title}</span>
                                                </div>
                                                <div className="text-xs text-[#52525B] mt-1">
                                                    {s.room}{s.floor ? ` · ${s.floor}` : ''}
                                                </div>
                                                {s.notes && (
                                                    <div className="text-xs text-[#09090B] mt-2 line-clamp-3">{s.notes}</div>
                                                )}
                                                {s.photo && (
                                                    <img src={s.photo} alt="" className="mt-2 w-full h-24 object-cover border border-[#E4E4E7]" />
                                                )}
                                                <div className={`mt-1 text-[10px] font-mono uppercase tracking-[0.18em] ${s.status === 'completed' ? 'text-[#00875A]' : 'text-[#E53935]'}`}>
                                                    {s.status}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {pinsOnFloor.length > 0 && <PinLegend position="bottom-right" testId="shared-pin-legend" />}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


const SEV_SHARED = {
    cosmetic: { bg: '#F4F4F5', fg: '#52525B', label: 'Cosmetic' },
    minor:    { bg: '#E8F5FB', fg: '#1787AB', label: 'Minor' },
    major:    { bg: '#FEF3C7', fg: '#92400E', label: 'Major' },
    urgent:   { bg: '#FEE2E2', fg: '#991B1B', label: 'URGENT' },
};

function SharedFilterBar({
    rooms, severity, setSeverity, room, setRoom,
    response, setResponse, sortBy, setSortBy, active, onReset,
}) {
    const sevOpts = [
        { v: 'all', label: 'All severities' },
        { v: 'urgent', label: 'Urgent' },
        { v: 'major', label: 'Major' },
        { v: 'minor', label: 'Minor' },
        { v: 'cosmetic', label: 'Cosmetic' },
    ];
    const respOpts = [
        { v: 'all', label: 'All responses' },
        { v: 'awaiting', label: 'Awaiting your response' },
        { v: 'rejected', label: 'Rejected' },
        { v: 'approved', label: 'Approved' },
        { v: 'disputed', label: 'Disputed' },
    ];
    const sortOpts = [
        { v: 'pin', label: 'Pin number' },
        { v: 'severity', label: 'Severity (urgent first)' },
        { v: 'recent', label: 'Most recent' },
    ];
    const baseSelect = "border border-[#E4E4E7] focus:border-[#18181B] outline-none px-2.5 py-2 bg-white text-xs font-mono uppercase tracking-[0.12em]";
    return (
        <div className="mt-6 mb-2 flex flex-wrap items-center gap-2 no-print" data-testid="shared-filter-bar">
            <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className={baseSelect}
                data-testid="filter-severity"
            >
                {sevOpts.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <select
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                className={baseSelect}
                data-testid="filter-response"
            >
                {respOpts.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            {rooms.length > 0 && (
                <select
                    value={room}
                    onChange={(e) => setRoom(e.target.value)}
                    className={baseSelect}
                    data-testid="filter-room"
                >
                    <option value="all">All rooms</option>
                    {rooms.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
            )}
            <span className="text-[#A1A1AA] text-xs ml-1">·</span>
            <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={baseSelect}
                data-testid="filter-sort"
            >
                {sortOpts.map(o => <option key={o.v} value={o.v}>Sort: {o.label}</option>)}
            </select>
            {active && (
                <button
                    type="button"
                    onClick={onReset}
                    data-testid="filter-reset"
                    className="px-2.5 py-2 border border-[#E4E4E7] hover:border-[#E53935] text-[#E53935] text-xs font-mono uppercase tracking-[0.12em]"
                >
                    Reset
                </button>
            )}
        </div>
    );
}

const RESP_LABEL = { owner: 'Owner', builder: 'Builder', specialist: 'Specialist' };

function SharedAiVerdict({ verdict, testId }) {
    const sev = SEV_SHARED[verdict.severity] || SEV_SHARED.minor;
    // ALWAYS render as a vertical stack — this card lives inside a narrow
    // flex column (photo thumbnail + locate button eat the row width) so
    // a horizontal grid would produce single-word-per-line ribbons even
    // on iPad. Cause is visible up-front; Fix / Cost / Who behind an
    // expand toggle so builders don't need to scroll past a wall of
    // text to get to the next defect.
    const [open, setOpen] = useState(false);
    return (
        <div
            className="mt-2 border border-[#1FA5CF] bg-[#E8F5FB] p-2.5 text-xs"
            data-testid={testId}
        >
            {/* Header strip: AI badge + severity pill + optional NHBC ref */}
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-mono text-[9px] uppercase tracking-wider font-bold text-[#1787AB]">AI Verdict</span>
                    <span
                        className="px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase"
                        style={{ background: sev.bg, color: sev.fg }}
                    >{sev.label}</span>
                </div>
                {verdict.nhbc_reference && (
                    <span className="text-[10px] font-mono uppercase tracking-wider text-[#52525B] truncate max-w-full" title={verdict.nhbc_reference}>
                        {verdict.nhbc_reference}
                    </span>
                )}
            </div>

            <div className="space-y-2">
                <AiRow label="Cause" value={verdict.cause} />
                {open && (
                    <>
                        <AiRow label="Fix" value={verdict.suggested_fix} />
                        <div className="grid grid-cols-2 gap-2">
                            <AiRow label="Cost" value={verdict.cost_band} mono compact />
                            <AiRow label="Who" value={RESP_LABEL[verdict.responsibility] || verdict.responsibility} mono compact />
                        </div>
                    </>
                )}
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    aria-expanded={open}
                    data-testid={`${testId}-toggle`}
                    className="w-full flex items-center justify-center gap-1 py-1 mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#1787AB] hover:text-[#0F4C5C] active:scale-95 transition-transform"
                >
                    {open
                        ? <>Hide details <CaretUp size={11} weight="bold" /></>
                        : <>Show fix &middot; cost &middot; who <CaretDown size={11} weight="bold" /></>}
                </button>
            </div>
        </div>
    );
}

/** Row layout: label above value so multi-sentence values stay readable
 *  at any container width (the AI verdict card is always inside a
 *  narrow flex column on the Shared portal). */
function AiRow({ label, value, mono = false, compact = false }) {
    return (
        <div className="min-w-0">
            <div className={`font-mono text-[9px] uppercase tracking-wider text-[#1787AB] ${compact ? 'mb-0' : 'mb-0.5'}`}>{label}</div>
            <div className={`text-[#09090B] text-[12px] leading-relaxed break-words ${mono ? 'font-mono uppercase tracking-wide text-[11px]' : ''}`}>
                {value || '—'}
            </div>
        </div>
    );
}

